(() => {
  const mqMobile = () => window.matchMedia('(max-width: 767px)').matches;

  function mountVideo(root) {
    if (!root) return;
    const wantMobile = mqMobile() && root.classList.contains('saby-video--has-mobile');
    const mode = wantMobile ? 'mobile' : 'desktop';
    if (root.dataset.mounted === mode) {
      playVideo(root);
      return;
    }
    root.querySelectorAll('video').forEach((v) => {
      try { v.pause(); } catch (e) {}
      v.remove();
    });
    const selector = wantMobile ? '.saby-video__tpl--mobile' : '.saby-video__tpl--desktop';
    const tpl = root.querySelector(selector) || root.querySelector('.saby-video__tpl');
    if (tpl && tpl.tagName === 'TEMPLATE') {
      root.appendChild(tpl.content.cloneNode(true));
    }
    root.dataset.mounted = mode;
    playVideo(root);
  }

  function playVideo(root) {
    const v = root && root.querySelector('video');
    if (!v) return;
    v.playsInline = true;
    v.muted = root.dataset.keepSound === '1' ? false : true;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
    root.classList.add('is-playing');
  }

  function pauseVideo(root) {
    if (!root) return;
    const v = root.querySelector('video');
    if (v) { try { v.pause(); } catch (e) {} }
    root.classList.remove('is-playing');
  }

  /* ---------- Shop the Look ---------- */
  if (!customElements.get('saby-shop-the-look')) {
    class ShopTheLook extends HTMLElement {
      connectedCallback() {
        this.panels = Array.from(this.querySelectorAll('[data-stl-panel]'));
        this.tabs = Array.from(this.querySelectorAll('[data-stl-tab]'));
        this.groups = Array.from(this.querySelectorAll('[data-stl-group]'));
        this.prevBtn = this.querySelector('.saby-stl__arrow--prev');
        this.nextBtn = this.querySelector('.saby-stl__arrow--next');
        this.index = 0;
        this.count = this.panels.length;
        if (!this.count) return;

        this.tabs.forEach((t, i) => t.addEventListener('click', () => this.select(i)));
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.select(this.index - 1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.select(this.index + 1));

        this._io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            const root = this.activeVideo();
            if (e.isIntersecting) { mountVideo(root); }
            else { pauseVideo(root); }
          });
        }, { rootMargin: '150px' });
        this._io.observe(this);

        this._resize = () => { const r = this.activeVideo(); if (this._inView()) mountVideo(r); };
        window.addEventListener('resize', this._resize);

        if (window.Shopify && Shopify.designMode) {
          this.addEventListener('shopify:block:select', (ev) => {
            const idx = this.panels.findIndex((p) => p.contains(ev.target) || p === ev.target);
            if (idx >= 0) this.select(idx);
          });
        }
      }
      disconnectedCallback() {
        if (this._io) this._io.disconnect();
        if (this._resize) window.removeEventListener('resize', this._resize);
      }
      _inView() {
        const r = this.getBoundingClientRect();
        return r.bottom > 0 && r.top < window.innerHeight;
      }
      activeVideo() {
        const panel = this.panels[this.index];
        return panel && panel.querySelector('[data-saby-video]');
      }
      select(i) {
        i = (i + this.count) % this.count;
        if (i === this.index) return;
        pauseVideo(this.activeVideo());
        this.index = i;
        this.panels.forEach((p, k) => p.classList.toggle('is-active', k === i));
        this.tabs.forEach((t, k) => {
          const on = k === i;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        this.groups.forEach((g, k) => g.classList.toggle('is-active', k === i));
        if (this._inView()) mountVideo(this.activeVideo());
      }
    }
    customElements.define('saby-shop-the-look', ShopTheLook);
  }

  /* ---------- Video Runway ----------
     Only the settled/selected cell loads+plays video.
     Posters are always in the DOM; video layer stays hidden until playing.
     No MutationObserver — that was thrashing the main thread. */
  function runwayEnsureSrc(cell) {
    if (!cell) return null;
    const wrap = cell.querySelector('[data-saby-runway-video]');
    if (!wrap) return null;
    const v = wrap.querySelector('video');
    if (!v) return null;
    const src = wrap.getAttribute('data-src');
    if (src && !v.getAttribute('src') && !v.querySelector('source')) {
      const source = document.createElement('source');
      source.src = src;
      source.type = 'video/mp4';
      v.appendChild(source);
      try { v.load(); } catch (e) {}
    }
    return v;
  }

  function runwayPlayCell(cell) {
    const wrap = cell && cell.querySelector('[data-saby-runway-video]');
    const v = runwayEnsureSrc(cell);
    if (!v || !wrap) return;
    v.muted = true;
    v.playsInline = true;
    const show = () => {
      v.classList.add('is-playing');
      wrap.classList.add('is-playing');
    };
    v.addEventListener('playing', show, { once: true });
    const p = v.play();
    if (p && typeof p.then === 'function') p.then(show).catch(() => {});
  }

  function runwayPauseCell(cell) {
    if (!cell) return;
    const wrap = cell.querySelector('[data-saby-runway-video]');
    const v = cell.querySelector('video');
    if (v) {
      try { v.pause(); } catch (e) {}
      v.classList.remove('is-playing');
    }
    if (wrap) wrap.classList.remove('is-playing');
    // Keep source attached — unloading/reloading every settle was freezing the page.
  }

  function runwayActiveCell(root) {
    return (
      root.querySelector('.saby-runway__cell.is-selected') ||
      root.querySelector('.saby-runway__cell.is-initial-selected')
    );
  }

  function bindRunwayLazy(root) {
    if (!root || root.dataset.sabyRunwayBound) return;
    root.dataset.sabyRunwayBound = '1';

    const cells = () => Array.from(root.querySelectorAll('.saby-runway__cell'));
    let inView = false;
    let current = null;

    const apply = () => {
      const next = inView ? runwayActiveCell(root) : null;
      if (next === current && next) {
        // Still the same cell — ensure it's playing if visible
        if (inView) runwayPlayCell(next);
        return;
      }
      if (current && current !== next) runwayPauseCell(current);
      current = next;
      if (current) runwayPlayCell(current);
      else cells().forEach(runwayPauseCell);
    };

    const onFlkty = (flkty) => {
      flkty.on('settle', apply);
      flkty.on('change', apply);
      apply();
    };

    const waitFlkty = () => {
      if (root._flkty) {
        onFlkty(root._flkty);
        return;
      }
      let n = 0;
      const id = setInterval(() => {
        n += 1;
        if (root._flkty) {
          clearInterval(id);
          onFlkty(root._flkty);
        } else if (n > 80) {
          clearInterval(id);
          apply();
        }
      }, 50);
    };

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        inView = e.isIntersecting;
        if (!inView) {
          if (current) runwayPauseCell(current);
          current = null;
          cells().forEach(runwayPauseCell);
        } else {
          apply();
        }
      });
    }, { rootMargin: '80px' });
    io.observe(root);

    waitFlkty();
  }

  function initRunways() {
    document.querySelectorAll('slide-show.saby-runway--lazy').forEach(bindRunwayLazy);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRunways);
  } else {
    initRunways();
  }
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target && e.target.querySelector && e.target.querySelector('slide-show.saby-runway--lazy');
    if (root) {
      delete root.dataset.sabyRunwayBound;
      bindRunwayLazy(root);
    } else {
      initRunways();
    }
  });
})();
